/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useRef } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

type UseCarouselInfiniteScrollParams<T> = {
  allItems: T[];
  loadCount: number;
  onLoadMore: (items: T[]) => void;
};

export const useCarouselInfiniteScroll = <T>({
  allItems,
  loadCount,
  onLoadMore,
}: UseCarouselInfiniteScrollParams<T>) => {
  const loadedCountRef = useRef(0);
  const lastTriggerRef = useRef(0);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const nativeEvent = e?.nativeEvent;
      if (!nativeEvent) return;

      const { contentSize, contentOffset, layoutMeasurement } = nativeEvent;

      if (
        !contentSize ||
        !layoutMeasurement ||
        typeof contentSize.width !== 'number' ||
        typeof layoutMeasurement.width !== 'number'
      ) {
        return;
      }

      const TOTAL_WIDTH = contentSize.width;
      const SCROLLED_X = contentOffset?.x ?? 0;
      const VIEWPORT_WIDTH = layoutMeasurement.width;

      if (TOTAL_WIDTH <= 0 || VIEWPORT_WIDTH <= 0) return;

      const triggerPoint = TOTAL_WIDTH - (3 * VIEWPORT_WIDTH) / 2;

      if (SCROLLED_X <= triggerPoint) return;
      if (lastTriggerRef.current >= triggerPoint) return;
      if (loadedCountRef.current >= allItems.length) return;

      const nextItems = allItems.slice(
        loadedCountRef.current,
        loadedCountRef.current + loadCount,
      );

      if (!nextItems.length) return;

      onLoadMore(nextItems);
      loadedCountRef.current += nextItems.length;
      lastTriggerRef.current = triggerPoint;
    },
    [allItems, loadCount, onLoadMore],
  );

  const reset = useCallback(() => {
    loadedCountRef.current = 0;
    lastTriggerRef.current = 0;
  }, []);

  return {
    handleScroll,
    loadedCountRef,
    reset,
  };
};
